import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Phone } from "lucide-react";
import { TID } from "@/lib/testIds";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { GopuramBand, OmMark } from "@/components/Ornaments";
import { api } from "@/lib/api";

const navItems = [
  { to: "/", labelKey: "nav.home", tid: TID.navHome },
  { to: "/about", labelKey: "nav.about", tid: TID.navAbout },
  { to: "/poojas", labelKey: "nav.poojas", tid: TID.navPoojas },
  { to: "/knowledge", labelKey: "nav.knowledge", tid: TID.navKnowledge },
  { to: "/gallery", labelKey: "nav.gallery", tid: TID.navGallery },
  { to: "/contact", labelKey: "nav.contact", tid: TID.navContact },
];

export function PublicLayout({ children }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-paper text-[color:var(--ink)] flex flex-col">
      <header className="nav-glass sticky top-0 z-40">
        <div className="container-x flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
            <OmMark size={40} className="text-[color:var(--brand)]" />
            <div className="leading-tight">
              <div className="font-heading text-lg md:text-xl">{t("common.appName")}</div>
              <div className="text-[10px] md:text-[11px] tracking-[0.22em] uppercase opacity-70 italic-serif">{t("common.tagline")}</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.tid}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors ${isActive ? "text-[color:var(--brand)] font-medium" : "hover:text-[color:var(--brand)]"}`}
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to="/book"
              data-testid={TID.navBook}
              className="hidden md:inline-flex items-center gap-2 px-5 py-2 rounded-full btn-brand text-sm font-medium"
            >
              {t("nav.book")}
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2"
              aria-label="Menu"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-[color:var(--border)] bg-[color:var(--bg)]">
            <div className="container-x py-4 flex flex-col gap-3">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`m-${item.tid}`}
                  className={({ isActive }) =>
                    `py-1.5 ${isActive ? "text-[color:var(--brand)] font-medium" : ""}`}
                >
                  {t(item.labelKey)}
                </NavLink>
              ))}
              <Link to="/book" className="mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full btn-brand text-sm" data-testid="m-book-btn">
                {t("nav.book")}
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer data-testid={TID.footer} className="bg-ink text-[#f3e7c8] relative">
        {/* Decorative gopuram band at the top of the footer */}
        <div className="text-[#f3d7a8]/40">
          <GopuramBand height={22} />
        </div>
        <div className="container-x py-14 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <OmMark size={44} className="text-[#f3d7a8]" />
              <div>
                <div className="font-heading text-2xl text-[#faf8f5]">{t("common.appName")}</div>
                <div className="text-[10px] tracking-[0.22em] uppercase opacity-70 italic-serif mt-0.5">{t("common.tagline")}</div>
              </div>
            </div>
            <p className="text-sm opacity-85 max-w-md leading-[1.85] italic-serif">{t("footer.tagline")}</p>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#f3d7a8] mb-4">{t("footer.quickLinks")}</div>
            <ul className="space-y-2 text-sm">
              {navItems.map((i) => (
                <li key={i.to}><Link to={i.to} className="hover:text-[#f3d7a8] opacity-85">{t(i.labelKey)}</Link></li>
              ))}
              <li><Link to="/book" className="hover:text-[#f3d7a8] opacity-85">{t("nav.book")}</Link></li>
              <li><Link to="/admin/login" className="hover:text-[#f3d7a8] opacity-60 text-xs" data-testid="admin-login-link">{t("nav.adminLogin")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#f3d7a8] mb-4">{t("footer.contact")}</div>
            <ul className="space-y-3 text-sm">
              {settings?.contact_phone && (
                <li className="flex gap-2 items-start"><Phone size={14} className="mt-1 opacity-70" /><a href={`tel:${settings.contact_phone.replace(/\s/g, "")}`} className="hover:text-[#f3d7a8]">{settings.contact_phone}</a></li>
              )}
              {settings?.contact_email && (
                <li><a href={`mailto:${settings.contact_email}`} className="hover:text-[#f3d7a8]">{settings.contact_email}</a></li>
              )}
              {settings?.contact_address && (
                <li className="opacity-85 leading-[1.7] italic-serif">{settings.contact_address}</li>
              )}
            </ul>
          </div>
        </div>
        <div className="border-t border-[#f3d7a8]/15 py-5">
          <div className="container-x text-xs opacity-65 flex flex-col md:flex-row gap-2 justify-between">
            <span>© {new Date().getFullYear()} {t("common.appName")}. {t("footer.rights")}</span>
            <span className="italic-serif">Trimbakeshwar · Nashik · Maharashtra · India</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
